import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {ApiLocationService} from '@services/api-location/api-location.service';
import {Observable} from 'rxjs';
import {HTTPS_REQUEST_OPTIONS} from '@constants/https-request-options/https-request-options';
import {PageItem} from '@models/sidebar/page-item';
import {BaseMenuItem} from '@models/sidebar/response/base-menu-item';
import {MenuNotification, OperationType} from '@models/sidebar/menu-notification';
import {MenuNotificationService} from '@services/menu-notification/menu-notification.service';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {NotificationService} from '@services/notification/notification.service';
import {TranslateService} from '@ngx-translate/core';
import {StatusEventGroupPagePayload} from '@models/pages/status-event-group-page/status-event-group-page-payload';
import {StatusEventPage} from '@models/sidebar/response/status-event-page';
import {LeftMenuService} from '@services/left-menu/left-menu.service';
import {GlobalHelperService} from '@services/global-helper/global-helper.service';

@Injectable({
  providedIn: 'root'
})
export class StatusEventGroupPageService {

  private SAVE_STATUS_EVENT_PAGE_URL_SUFFIX = '/api/web/base/:machineGroupId/machineGroupStatusEventPage/:pageId';
  private STATUS_EVENT_PAGE_URL_SUFFIX: string = '/api/web/base/:machineGroupId/machineGroupStatusEventPage/:pageId/baseId/:itemId';

  private savingIndicatorMessage = this.translateService.instant('Saving');
  private successRenamingPage = this.translateService.instant('Status page was renamed.');
  private errorRenamingPage = this.translateService.instant('Error renaming status page.');
  private readonly BASE_URL: string;
  private successCreatingPage: string = this.translateService.instant('Status page was created.');

  constructor(
    private http: HttpClient,
    private apiLocation: ApiLocationService,
    private menuNotificationService: MenuNotificationService,
    private loadingIndicator: LoadingIndicatorService,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private leftMenuService: LeftMenuService,
    private globalHelper: GlobalHelperService,
  ) {
    this.BASE_URL = this.apiLocation.getLocation();
  }

  public create(baseMenuItems: PageItem[], pageItem: PageItem, groupId: string | null, currentPageName: string, parentId: string | null, isAdminOrHigher: boolean): void {
    if (groupId && parentId) {
      const msg: string = this.translateService.instant('Saving');
      this.loadingIndicator.show(msg);
      const errorPage = new StatusEventGroupPagePayload({id: -1, name: pageItem.name, machineGroupId: +groupId});
      this.saveEventStatusPage(groupId, '-1', errorPage).subscribe((baseMenuItem: BaseMenuItem) => {
        this.handleCreatedPage(baseMenuItem, pageItem, baseMenuItems, parentId, isAdminOrHigher, currentPageName);
      }, (error) => {
        const msg: string = this.translateService.instant('Error creating status page.');
        this.notificationService.error(msg, error, currentPageName, true);
        this.loadingIndicator.hide();
      });
    }
  }

  public rename(pageItem: PageItem, machineGroupId: number | null, parentId: string | null, currentPageName: string, nameFallback: Object): void {
    if (machineGroupId && pageItem.id && pageItem.pageId && parentId) {
      this.loadingIndicator.show(this.savingIndicatorMessage);
      const errorTab = new StatusEventGroupPagePayload({id: +pageItem.pageId, machineGroupId: machineGroupId, name: pageItem.name});
      const itemId: string = pageItem.id;
      this.renameEventStatusGroupPage(machineGroupId, itemId, pageItem.pageId, errorTab).subscribe((result: BaseMenuItem) => {
        this.globalHelper.handleUpdatedPage(result.name, parentId, currentPageName, itemId, this.successRenamingPage);
      }, (error) => {
        this.leftMenuService.broadcastErrorUpdatePageChange(nameFallback);
        this.notificationService.error(this.errorRenamingPage, error, currentPageName, true);
        this.loadingIndicator.hide();
      });
    }
  }

  private handleCreatedPage(createdItem: BaseMenuItem, pageItem: PageItem, baseMenuItems: PageItem[], parentId: string, isAdminOrHigher: boolean, currentPageName: string) {
    this.menuNotificationService.broadcast(new MenuNotification(OperationType.ADD, parentId, createdItem.name, ''));
    pageItem.id = createdItem.id;
    pageItem.canBeAltered = isAdminOrHigher;
    pageItem.parent = createdItem.parent;
    pageItem.pageId = createdItem.pageId;
    pageItem.index = createdItem.index;
    baseMenuItems.push(pageItem);
    this.loadingIndicator.hide();
    this.notificationService.success(this.successCreatingPage, currentPageName, false);
  }

  private renameEventStatusGroupPage(machineGroupId: number, itemId: string, pageId: string, request: StatusEventGroupPagePayload): Observable<any> {
    let url: string = this.BASE_URL + this.STATUS_EVENT_PAGE_URL_SUFFIX;
    url = url.replace(':machineGroupId', String(machineGroupId))
      .replace(':pageId', pageId)
      .replace(':itemId', itemId);
    return this.http.post<StatusEventPage>(url, request, HTTPS_REQUEST_OPTIONS);
  }

  private saveEventStatusPage(groupId: string, pageId: string, request: StatusEventGroupPagePayload): Observable<any> {
    let url: string = this.BASE_URL + this.SAVE_STATUS_EVENT_PAGE_URL_SUFFIX;
    url = url.replace(':machineGroupId', groupId).replace(':pageId', pageId);
    return this.http.post(url, request, HTTPS_REQUEST_OPTIONS);
  }

  public delete(machineGroupId: number, pageId: string, itemId: string): Observable<any> {
    let url: string = this.BASE_URL + this.STATUS_EVENT_PAGE_URL_SUFFIX;
    url = url.replace(':machineGroupId', String(machineGroupId))
      .replace(':pageId', pageId)
      .replace(':itemId', itemId);
    return this.http.delete(url, HTTPS_REQUEST_OPTIONS);
  }
}
